import React from 'react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

const IMG_HERD = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=2000&q=80';
const IMG_LINE = '/press/cheese-maturation.jpg';

const Eyebrow = ({ children }) => (
  <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>{children}</div>
);

export default function PressPage() {
  return (
    <SiteLayout>
      <SEO
        title="Press & Media Kit · Keystone by CloudVoro"
        description="The story, the numbers, the founder bio and the brand assets. How a Tipperary cheesemaker cut recall trace time from 2 hours to 30 seconds, ready for journalists."
        path="/press"
        keywords="Keystone press kit, CloudVoro media, Cashel Blue software story, Irish food tech press, food traceability news"
      />

      {/* HERO */}
      <section data-testid="press-hero" style={{ background: PALETTE.cream, padding: '160px 32px 80px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Eyebrow>Press & media · self-serve kit</Eyebrow>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(42px, 5.6vw, 72px)',
            fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0, maxWidth: 900,
          }}>
            The story, the numbers,<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>no press-release padding.</em>
          </h1>
          <p style={{ fontSize: 19, lineHeight: 1.55, color: PALETTE.slate, marginTop: 26, maxWidth: 660 }}>
            Everything a journalist needs to cover Keystone without waiting for us: the pitch, verifiable numbers, founder bio, boilerplate and brand assets. Interviews and production-floor photography arranged within a working day.
          </p>
        </div>
      </section>

      {/* THE STORY */}
      <section style={{ background: PALETTE.paper, padding: '80px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 40, alignItems: 'start' }}>
          <div data-testid="press-story">
            <Eyebrow>The story</Eyebrow>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 40px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              A Tipperary cheesemaker cut recall trace time from 2 hours to 30 seconds.
            </h2>
            <div style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 20 }}>
              <p style={{ marginTop: 0 }}>
                Cashel Farmhouse Cheesemakers (Cashel Blue) used to answer a traceability question, “which batches went into last week’s delivery?”, by opening three spreadsheets. It took up to two hours. Under BRCGS rules they have four, and retailers tend to ask on a Friday afternoon.
              </p>
              <p>
                They now answer it in about 30 seconds, using software that was literally written on their premises. The developer, Asif Khan, spent 15 years building enterprise systems and moved into their cheese house to build something a family food business could actually afford.
              </p>
              <p style={{ marginBottom: 0 }}>
                The wider story: most small European food producers still run traceability on Excel, while the ERP quotes they receive start at €40,000. There is a gap in the market, and a compliance clock ticking on it.
              </p>
            </div>
            <div style={{ marginTop: 24, padding: '18px 22px', background: PALETTE.bgSoft, borderLeft: `3px solid ${PALETTE.magenta}` }}>
              <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 8 }}>We can offer</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14.5, lineHeight: 1.7, color: PALETTE.slate }}>
                <li>Interview: Louis Clifton Brown, QA & Operations Manager, Cashel Blue</li>
                <li>Interview: Asif Khan, founder, CloudVoro</li>
                <li>Production-floor photography from the creamery in Fethard, Co. Tipperary</li>
                <li>The measured before-and-after numbers, verifiable on site</li>
              </ul>
            </div>
          </div>
          <div>
            <img src={IMG_HERD} alt="Dairy herd in an Irish pasture" style={{ width: '100%', height: 300, objectFit: 'cover', display: 'block' }} />
            <img src={IMG_LINE} alt="Cheese wheels maturing on wooden shelves" style={{ width: '100%', height: 300, objectFit: 'cover', display: 'block', marginTop: 16 }} />
            <div style={{ fontSize: 12.5, color: PALETTE.slate, marginTop: 10 }}>
              Illustrative imagery. Original photography of the Cashel Blue creamery and the software in use is available on request, cleared for editorial use.
            </div>
          </div>
        </div>
      </section>

      {/* QUICK FACTS + FOUNDER BIO */}
      <section style={{ background: PALETTE.navy, padding: '80px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>Quick facts</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }} data-testid="press-quick-facts">
            {[
              ['Company', 'CloudVoro, headquartered in Ireland. Sister company to Intellix IT Solutions (UK).'],
              ['Product', 'Keystone: operations software for European food manufacturers. Traceability, production, dispatch, retailer EDI, COAs.'],
              ['Founding partner', 'Cashel Farmhouse Cheesemakers (Cashel Blue), live in production since 2024. 40+ SKUs, 9 export markets.'],
              ['The number', 'Recall trace time cut from around 2 hours to about 30 seconds, measured on site.'],
              ['Pricing', 'Published openly at keystone.cloudvoro.com/site/pricing. Fixed-fee implementation, live in 30 days.'],
              ['Languages', 'Product and site localized in English, Hungarian, Polish and German.'],
            ].map(([t, b]) => (
              <div key={t} style={{ padding: '20px', border: '1px solid rgba(255,255,255,0.14)' }}>
                <div style={{ fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold }}>{t}</div>
                <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#E5E7EB', marginTop: 8 }}>{b}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 48, padding: '28px 30px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)' }} data-testid="press-founder-bio">
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 12 }}>Founder bio</div>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, color: '#fff' }}>Asif Khan</div>
            <p style={{ fontSize: 15.5, lineHeight: 1.7, color: '#E5E7EB', marginTop: 12, marginBottom: 0, maxWidth: 820 }}>
              Asif Khan is the founder of CloudVoro and Director & Principal Consultant at Intellix IT Solutions (UK). He spent 15+ years delivering enterprise IT, including a multi-million-euro Microsoft Dynamics AX rollout for Kaspersky, SAP ECC implementations, and secure infrastructure for defence and embassy estates, before moving into a Tipperary cheese house in 2024 to build Keystone beside the people who would use it. He implements every customer personally and answers the phone number on this site himself.
            </p>
          </div>

          <div style={{ marginTop: 24, padding: '22px 26px', background: 'rgba(233,30,99,0.12)', border: `1px solid ${PALETTE.magenta}66` }} data-testid="press-boilerplate">
            <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 10 }}>Boilerplate, copy-paste ready</div>
            <p style={{ fontSize: 14.5, lineHeight: 1.7, color: '#E5E7EB', margin: 0, fontStyle: 'italic' }}>
              “Keystone by CloudVoro is operations software for European food manufacturers, covering batch traceability, production, dispatch, retailer EDI and audit evidence in one system. Built in Ireland beside the vat at Cashel Farmhouse Cheesemakers, it cuts recall trace times from hours to seconds and goes live in 30 days with founder-led implementation. Pricing is published at keystone.cloudvoro.com.”
            </p>
          </div>
        </div>
      </section>

      {/* DOWNLOADS: logo assets for press */}
      <section style={{ background: PALETTE.cream, padding: '80px 32px' }} data-testid="press-downloads">
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <Eyebrow>Brand assets</Eyebrow>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 40px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            Take the logos.<br/><em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Please don&apos;t recolour or stretch them.</em>
          </h2>
          <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            <div style={{ padding: '26px 24px', background: PALETTE.paper, border: `1px solid ${PALETTE.border}`, borderTop: `3px solid ${PALETTE.navy}` }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: PALETTE.navy }}>Logo pack</div>
              <div style={{ fontSize: 14, color: PALETTE.slate, lineHeight: 1.6, margin: '8px 0 14px' }}>
                Keystone marks and lockups in light and dark, PNG and SVG.
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, padding: '12px', background: PALETTE.bgSoft }}>
                <img src="/brochure-assets/logos/keystone-mark-256.png" alt="Keystone mark" style={{ height: 44 }} />
                <img src="/brochure-assets/logos/keystone-mark-mono-navy-256.png" alt="Keystone mark, navy" style={{ height: 44 }} />
                <span style={{ background: PALETTE.navy, padding: 8, display: 'inline-flex' }}>
                  <img src="/brochure-assets/logos/keystone-mark-white-256.png" alt="Keystone mark, white" style={{ height: 36 }} />
                </span>
              </div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, fontWeight: 600 }}>
                <a href="/brochure-assets/logos/keystone-mark-2048.png" download data-testid="press-logo-png" style={{ color: PALETTE.magenta }}>Mark PNG</a>
                <a href="/brochure-assets/logos/keystone-mark.svg" download style={{ color: PALETTE.magenta }}>Mark SVG</a>
                <a href="/brochure-assets/logos/keystone-horizontal-light-2048.png" download style={{ color: PALETTE.magenta }}>Lockup light</a>
                <a href="/brochure-assets/logos/keystone-horizontal-dark-2048.png" download style={{ color: PALETTE.magenta }}>Lockup dark</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRESS CONTACT */}
      <section style={{ background: PALETTE.paper, padding: '70px 32px', borderTop: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }} data-testid="press-contact">
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em' }}>
            Press contact
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.65, color: PALETTE.slate, marginTop: 14 }}>
            No comms agency, you reach the founder directly. Email <a href="mailto:hello@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>hello@cloudvoro.com</a> with “Press” in the subject or call <a href="tel:+353851810310" style={{ color: PALETTE.magenta, fontWeight: 600 }}>+353 85 181 0310</a>. Replies within a working day, interviews and photography usually arranged within two.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
